import { Button } from "@/components/button"
import { NfpmPendingNotice } from "@/domains/thick-v1/components/nfpm-pending-notice"
import { cn, dollarsPrettified } from "@/domains/library"

export type CollectFeesViewProps = {
  token0Symbol: string
  token1Symbol: string
  /** Pre-formatted fee amounts (already `token.formatUnits` applied). */
  fee0Display: string
  fee1Display: string
  /**
   * Per-token USD value of the pending fees. `undefined` renders a muted dash
   * so "stats RPC has not replied yet" is visually distinct from "exactly
   * zero." Both values share the same price source as `claimableFeesUsd`.
   */
  fee0Usd: number | undefined
  fee1Usd: number | undefined
  claimableFeesUsd: number
  canSubmit: boolean
  isSubmitting: boolean
  submissionError?: string
  onSubmit: () => void
  /**
   * True while the on-chain NFPM tokenId is still being discovered. When set,
   * the view renders an inline status notice and the primary button label
   * reflects the wait instead of silently remaining disabled.
   */
  nfpmTokenIdPending?: boolean
}

/**
 * Pure collect-fees view.
 *
 * Mirrors Uniswap v3's "Collect fees" modal body: token-by-token breakdown
 * of pending fees, a USD total, and a submit button. No MobX coupling, no
 * hooks beyond transient DOM concerns (there are none here).
 */
export const CollectFeesView = ({
  token0Symbol,
  token1Symbol,
  fee0Display,
  fee1Display,
  fee0Usd,
  fee1Usd,
  claimableFeesUsd,
  canSubmit,
  isSubmitting,
  submissionError,
  onSubmit,
  nfpmTokenIdPending = false,
}: CollectFeesViewProps) => {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-t100 text-base font-medium">Collect fees</h2>
        <span className="text-t300 text-xs">{dollarsPrettified(claimableFeesUsd)} unclaimed</span>
      </div>

      <div className="flex flex-col gap-2">
        <FeeRow symbol={token0Symbol} amount={fee0Display} usd={fee0Usd} />
        <FeeRow symbol={token1Symbol} amount={fee1Display} usd={fee1Usd} />
      </div>

      <p className="text-t300 text-xs">
        Collecting fees withdraws the pending amounts to your wallet. It does not close the
        position or remove liquidity.
      </p>

      {nfpmTokenIdPending ? <NfpmPendingNotice actionLabel="Collect" /> : null}

      {submissionError ? (
        <p className="rounded-md border border-error-border/40 bg-error-background/10 px-3 py-2 text-xs text-error-text">
          {submissionError}
        </p>
      ) : null}

      <Button
        variant={"primary"}
        size={"lg"}
        onClick={onSubmit}
        disabled={!canSubmit || isSubmitting}
        className={cn("w-full")}
      >
        {isSubmitting
          ? "Collecting..."
          : nfpmTokenIdPending
            ? "Resolving NFP id…"
            : canSubmit
              ? "Collect"
              : "Nothing to collect"}
      </Button>
    </div>
  )
}

const FeeRow = ({
  symbol,
  amount,
  usd,
}: {
  symbol: string
  amount: string
  usd: number | undefined
}) => (
  <div className="flex items-center justify-between rounded-md bg-p300 border border-p300-light px-3 py-2">
    <span className="text-t200 text-sm">{symbol}</span>
    <div className="flex flex-col items-end">
      <span className="text-t100 text-sm font-medium">{amount}</span>
      <span className="text-t300 text-[11px]">
        {usd === undefined ? "—" : dollarsPrettified(usd)}
      </span>
    </div>
  </div>
)
